import React, { useState, useEffect, useCallback } from 'react';
import { Settings, BarChart3, Moon, Sun, Maximize, Minimize, RotateCcw, Copy, Download, Upload, Shuffle, Shield, Info, Grid3X3, Key, BookOpen } from 'lucide-react';
import { Helmet } from 'react-helmet-async';

interface CipherStats {
  totalEncryptions: number;
  totalDecryptions: number;
  totalOperations: number;
  totalCharactersProcessed: number;
  averageTextLength: number;
  longestText: number;
  polyalphabeticOperations: number;
  totalTablesUsed: number;
  uniqueKeys: Set<string>;
  averageKeyLength: number;
}

interface CipherHistory {
  id: string;
  input: string;
  output: string;
  key: string;
  operation: 'encrypt' | 'decrypt';
  timestamp: Date;
  textLength: number;
  keyRepeats: number;
}

const PortaCipherApp: React.FC = () => {
  // Core state
  const [inputText, setInputText] = useState<string>('');
  const [outputText, setOutputText] = useState<string>('');
  const [cipherKey, setCipherKey] = useState<string>('PORTA');
  const [operation, setOperation] = useState<'encrypt' | 'decrypt'>('encrypt');
  
  // UI state
  const [isDarkMode, setIsDarkMode] = useState<boolean>(false);
  const [isFullscreen, setIsFullscreen] = useState<boolean>(false);
  const [activeModal, setActiveModal] = useState<'settings' | 'analytics' | 'help' | 'table' | null>(null);
  
  // Advanced options state
  const [preserveCase, setPreserveCase] = useState<boolean>(true);
  const [preserveSpaces, setPreserveSpaces] = useState<boolean>(false);
  const [preservePunctuation, setPreservePunctuation] = useState<boolean>(false);
  const [showTable, setShowTable] = useState<boolean>(false);
  const [groupOutput, setGroupOutput] = useState<boolean>(true);
  const [groupSize, setGroupSize] = useState<number>(5);
  const [keyIndicators, setKeyIndicators] = useState<boolean>(true);
  const [showKeyMapping, setShowKeyMapping] = useState<boolean>(true);
  
  // Analytics state
  const [stats, setStats] = useState<CipherStats>({
    totalEncryptions: 0,
    totalDecryptions: 0,
    totalOperations: 0,
    totalCharactersProcessed: 0,
    averageTextLength: 0,
    longestText: 0,
    polyalphabeticOperations: 0,
    totalTablesUsed: 0,
    uniqueKeys: new Set<string>(),
    averageKeyLength: 0
  });
  const [history, setHistory] = useState<CipherHistory[]>([]);
  const [portaTable, setPortaTable] = useState<string[][]>([]);

  // Generate Porta cipher table
  const generatePortaTable = useCallback((): string[][] => {
    // Porta cipher uses 13 alphabets (A-M maps to keys)
    const alphabets = [
      'NOPQRSTUVWXYZABCDEFGHIJKLM', // A, B
      'OPQRSTUVWXYZNMABCDEFGHIJKL', // C, D
      'PQRSTUVWXYZNOLMABCDEFGHIJK', // E, F
      'QRSTUVWXYZNOPKLMABCDEFGHIJ', // G, H
      'RSTUVWXYZNOPQJKLMABCDEFGHI', // I, J
      'STUVWXYZNOPQRIJKLMABCDEFGH', // K, L
      'TUVWXYZNOPQRSHIJKLMABCDEFG', // M, N
      'UVWXYZNOPQRSTGHIJKLMABCDEF', // O, P
      'VWXYZNOPQRSTUFGHIJKLMABCDE', // Q, R
      'WXYZNOPQRSTUVEFGHIJKLMABCD', // S, T
      'XYZNOPQRSTUVWDEFGHIJKLMABC', // U, V
      'YZNOPQRSTUVWXCDEFGHIJKLMAB', // W, X
      'ZNOPQRSTUVWXYBCDEFGHIJKLMA'  // Y, Z
    ];
    
    const table: string[][] = [];
    for (let i = 0; i < 13; i++) {
      table[i] = alphabets[i].split('');
    }
    
    return table;
  }, []);

  // Get alphabet index for key character
  const getAlphabetIndex = useCallback((keyChar: string): number => {
    const charCode = keyChar.charCodeAt(0) - 65;
    return Math.floor(charCode / 2); // A,B=0, C,D=1, E,F=2, etc.
  }, []);

  // Generate extended key
  const generateExtendedKey = useCallback((text: string, key: string): string => {
    if (!key || !text) return '';
    
    const cleanText = text.replace(/[^A-Z]/g, '').toUpperCase();
    const cleanKey = key.replace(/[^A-Z]/g, '').toUpperCase();
    
    let extendedKey = '';
    for (let i = 0; i < cleanText.length; i++) {
      extendedKey += cleanKey[i % cleanKey.length];
    }
    return extendedKey;
  }, []);

  // Prepare text for Porta cipher
  const prepareText = useCallback((text: string): string => {
    return text.replace(/[^A-Z]/g, '').toUpperCase();
  }, []);

  // Porta cipher encryption/decryption (reciprocal)
  const portaProcess = useCallback((text: string, key: string): string => {
    if (!text || !key) return '';
    
    const table = generatePortaTable();
    setPortaTable(table);
    
    const preparedText = prepareText(text);
    const extendedKey = generateExtendedKey(preparedText, key);
    let result = '';
    
    // Process each character
    for (let i = 0; i < preparedText.length; i++) {
      const plainChar = preparedText[i];
      const keyChar = extendedKey[i];
      
      if (!keyChar) break;
      
      const plainIndex = plainChar.charCodeAt(0) - 65;
      const alphabetIndex = getAlphabetIndex(keyChar);
      
      if (plainIndex >= 0 && plainIndex < 26 && alphabetIndex >= 0 && alphabetIndex < 13) {
        // Porta cipher is reciprocal - same process for encrypt/decrypt
        result += table[alphabetIndex][plainIndex];
      }
    }
    
    // Format output
    if (groupOutput && result) {
      const groups = [];
      for (let i = 0; i < result.length; i += groupSize) {
        groups.push(result.slice(i, i + groupSize));
      }
      result = groups.join(' ');
    }
    
    return result;
  }, [generatePortaTable, prepareText, generateExtendedKey, getAlphabetIndex, groupOutput, groupSize]);

  // Process text - main cipher logic
  const processText = useCallback(() => {
    if (!cipherKey.trim()) {
      setOutputText('');
      return '';
    }
    
    // Porta cipher is reciprocal - same process for both operations
    const result = portaProcess(inputText, cipherKey);
    setOutputText(result);
    return result;
  }, [inputText, cipherKey, portaProcess]);

  // Update analytics
  const updateAnalytics = useCallback((result: string) => {
    if (!inputText || !cipherKey.trim() || !result) return;
    
    const cleanInput = inputText.replace(/[^A-Z]/g, '').toUpperCase();
    const keyRepeats = Math.ceil(cleanInput.length / cipherKey.replace(/[^A-Z]/g, '').length);
    
    const newHistoryItem: CipherHistory = {
      id: Date.now().toString(),
      input: inputText,
      output: result,
      key: cipherKey,
      operation,
      timestamp: new Date(),
      textLength: inputText.length,
      keyRepeats: keyRepeats
    };
    
    setHistory(prev => [newHistoryItem, ...prev.slice(0, 49)]);
    
    setStats(prevStats => {
      const newUniqueKeys = new Set(Array.from(prevStats.uniqueKeys).concat([cipherKey.toUpperCase()]));
      const totalOps = prevStats.totalOperations + 1;
      const totalChars = prevStats.totalCharactersProcessed + inputText.length;
      
      return {
        ...prevStats,
        totalEncryptions: prevStats.totalEncryptions + (operation === 'encrypt' ? 1 : 0),
        totalDecryptions: prevStats.totalDecryptions + (operation === 'decrypt' ? 1 : 0),
        totalOperations: totalOps,
        totalCharactersProcessed: totalChars,
        averageTextLength: Math.round(totalChars / totalOps),
        longestText: Math.max(prevStats.longestText, inputText.length),
        polyalphabeticOperations: prevStats.polyalphabeticOperations + 1,
        totalTablesUsed: prevStats.totalTablesUsed + 1,
        uniqueKeys: newUniqueKeys,
        averageKeyLength: Math.round(Array.from(newUniqueKeys).reduce((sum, k) => sum + k.length, 0) / newUniqueKeys.size)
      };
    });
  }, [inputText, cipherKey, operation]);

  // Auto-process when inputs change
  useEffect(() => {
    if (inputText && cipherKey) {
      const result = processText();
      if (result) {
        updateAnalytics(result);
      }
    } else {
      setOutputText('');
    }
  }, [inputText, cipherKey, operation]);

  // Update table when component mounts
  useEffect(() => {
    setPortaTable(generatePortaTable());
  }, [generatePortaTable]);

  // Utility functions
  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
    } catch (err) {
      console.error('Failed to copy text');
    }
  };

  const downloadText = (text: string, filename: string) => {
    const blob = new Blob([text], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const content = e.target?.result as string;
        setInputText(content);
      };
      reader.readAsText(file);
    }
  };

  const resetAll = () => {
    setInputText('');
    setOutputText('');
    setCipherKey('PORTA');
    setOperation('encrypt');
  };

  const toggleFullscreen = () => {
    setIsFullscreen(!isFullscreen);
  };

  const swapTexts = () => {
    const temp = inputText;
    setInputText(outputText.replace(/\s/g, ''));
    setOutputText(temp);
    setOperation(operation === 'encrypt' ? 'decrypt' : 'encrypt');
  };

  const generateSampleText = () => {
    const samples = [
      "ATTACK AT DAWN FROM THE NORTH",
      "THE PORTA CIPHER IS RECIPROCAL",
      "RENAISSANCE CRYPTOGRAPHY WORKS",
      "GIOVANNI BATTISTA PORTA INVENTED",
      "POLYALPHABETIC SUBSTITUTION CIPHER",
      "THIRTEEN ALPHABETS FOR ENCRYPTION",
      "CLASSICAL ITALIAN CRYPTOGRAPHY"
    ];
    const randomSample = samples[Math.floor(Math.random() * samples.length)];
    setInputText(randomSample);
  };

  const generateRandomKey = () => {
    const words = [
      'PORTA', 'GIOVANNI', 'CIPHER', 'SECRET', 'CRYPTO', 'RENAISSANCE',
      'ITALIAN', 'POLYALPHA', 'ENCODE', 'DECODE', 'HIDDEN', 'SECURITY',
      'BATTISTA', 'CLASSIC', 'SUBSTITUTION', 'RECIPROCAL'
    ];
    const randomWord = words[Math.floor(Math.random() * words.length)];
    setCipherKey(randomWord);
  };

  // Get key group for display
  const getKeyGroup = (keyChar: string): string => {
    const pairs = ['AB', 'CD', 'EF', 'GH', 'IJ', 'KL', 'MN', 'OP', 'QR', 'ST', 'UV', 'WX', 'YZ'];
    const alphabetIndex = getAlphabetIndex(keyChar);
    return pairs[alphabetIndex] || '';
  };

  return (
    <>
      <Helmet>
        <title>Porta Cipher - Renaissance Polyalphabetic Substitution Tool | Free Online Cryptography</title>
        <meta name="description" content="Professional Porta Cipher tool with reciprocal polyalphabetic substitution, Renaissance cryptography features, and Giovanni Battista Porta's historical cipher system." />
        <meta name="keywords" content="porta cipher, polyalphabetic cipher, reciprocal cipher, renaissance cipher, giovanni battista porta, italian cipher, classical cryptography, 13 alphabets, renaissance cryptography, historical encryption" />
        <meta name="theme-color" content={isDarkMode ? '#1f2937' : '#ffffff'} />
      </Helmet>

      <div className={`min-h-screen transition-colors duration-300 ${
        isDarkMode ? 'bg-gray-900 text-white' : 'bg-gray-50 text-gray-900'
      } ${isFullscreen ? 'fixed inset-0 z-50' : ''}`}>
        
        {/* Header */}
        <header className={`${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} border-b px-6 py-4`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <BookOpen className="text-indigo-500" size={24} />
              <div>
                <h1 className="text-2xl font-bold">Porta Cipher</h1>
                <p className="text-sm opacity-60">Renaissance Polyalphabetic Substitution Cipher (1563)</p>
              </div>
            </div>
            
            <div className="flex items-center space-x-2">
              <button
                onClick={() => setActiveModal(activeModal === 'table' ? null : 'table')}
                className={`p-2 rounded-lg transition-colors ${
                  isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'
                }`}
                title="View Porta Table"
              >
                <Grid3X3 size={20} />
              </button>
              
              <button
                onClick={() => setActiveModal(activeModal === 'help' ? null : 'help')}
                className={`p-2 rounded-lg transition-colors ${
                  isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'
                }`}
                title="Help & Information"
              >
                <Info size={20} />
              </button>
              
              <button
                onClick={() => setActiveModal(activeModal === 'settings' ? null : 'settings')}
                className={`p-2 rounded-lg transition-colors ${
                  isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'
                }`}
                title="Advanced Options"
              >
                <Settings size={20} />
              </button>
              
              <button
                onClick={() => setActiveModal(activeModal === 'analytics' ? null : 'analytics')}
                className={`p-2 rounded-lg transition-colors ${
                  isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'
                }`}
                title="Analytics"
              >
                <BarChart3 size={20} />
              </button>
              
              <button
                onClick={() => setIsDarkMode(!isDarkMode)}
                className={`p-2 rounded-lg transition-colors ${
                  isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'
                }`}
                title="Toggle Theme"
              >
                {isDarkMode ? <Sun size={20} /> : <Moon size={20} />}
              </button>
              
              <button
                onClick={toggleFullscreen}
                className={`p-2 rounded-lg transition-colors ${
                  isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'
                }`}
                title="Toggle Fullscreen"
              >
                {isFullscreen ? <Minimize size={20} /> : <Maximize size={20} />}
              </button>
            </div>
          </div>
        </header>

        {/* Main Content */}
        <main className="flex-1 p-6">
          <div className="max-w-6xl mx-auto space-y-6">
            
            {/* Controls */}
            <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg p-6 shadow-lg`}>
              <div className="grid grid-cols-1 md:grid-cols-6 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Operation</label>
                  <select
                    value={operation}
                    onChange={(e) => setOperation(e.target.value as 'encrypt' | 'decrypt')}
                    className={`w-full p-2 rounded border ${
                      isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-300'
                    }`}
                  >
                    <option value="encrypt">📜 Encrypt</option>
                    <option value="decrypt">🔓 Decrypt</option>
                  </select>
                </div>
                
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-sm font-medium">Cipher Key</label>
                    <button
                      onClick={generateRandomKey}
                      className={`text-xs px-2 py-1 rounded ${
                        isDarkMode ? 'bg-indigo-600 hover:bg-indigo-700' : 'bg-indigo-500 hover:bg-indigo-600'
                      } text-white transition-colors`}
                      title="Generate Random Key"
                    >
                      Random
                    </button>
                  </div>
                  <input
                    type="text"
                    value={cipherKey}
                    onChange={(e) => setCipherKey(e.target.value.replace(/[^a-zA-Z]/g, '').toUpperCase())}
                    placeholder="Enter cipher key..."
                    className={`w-full p-2 rounded border ${
                      isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-300'
                    }`}
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium mb-2">Reciprocal</label>
                  <div className={`w-full p-2 rounded border text-center ${
                    isDarkMode ? 'bg-gray-700 border-gray-600 text-green-400' : 'bg-green-50 border-green-300 text-green-600'
                  }`}>
                    ✓ Same Process
                  </div>
                </div>
                
                <div className="flex items-end">
                  <button
                    onClick={swapTexts}
                    className={`w-full p-2 rounded border flex items-center justify-center space-x-2 ${
                      isDarkMode ? 'bg-indigo-600 border-indigo-500 hover:bg-indigo-700 text-white' : 'bg-indigo-500 border-indigo-400 hover:bg-indigo-600 text-white'
                    } transition-colors`}
                    title="Swap input and output"
                  >
                    <Shuffle size={16} />
                    <span>Swap</span>
                  </button>
                </div>
                
                <div className="flex items-end">
                  <button
                    onClick={generateSampleText}
                    className={`w-full p-2 rounded border flex items-center justify-center space-x-2 ${
                      isDarkMode ? 'bg-green-600 border-green-500 hover:bg-green-700 text-white' : 'bg-green-500 border-green-400 hover:bg-green-600 text-white'
                    } transition-colors`}
                    title="Generate sample text"
                  >
                    <Key size={16} />
                    <span>Sample</span>
                  </button>
                </div>
                
                <div className="flex items-end">
                  <button
                    onClick={resetAll}
                    className={`w-full p-2 rounded border flex items-center justify-center space-x-2 ${
                      isDarkMode ? 'bg-gray-700 border-gray-600 hover:bg-gray-600' : 'bg-gray-100 border-gray-300 hover:bg-gray-200'
                    } transition-colors`}
                  >
                    <RotateCcw size={16} />
                    <span>Reset</span>
                  </button>
                </div>
              </div>
              
              {/* Key Information */}
              {keyIndicators && cipherKey && inputText && (
                <div className={`p-4 rounded ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'} mb-4`}>
                  <h4 className="text-sm font-medium mb-3">Key Analysis:</h4>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-xs">
                    <div>
                      <div className="font-medium mb-1">Key: {cipherKey}</div>
                      <div className="opacity-60">Length: {cipherKey.replace(/[^A-Z]/g, '').length} characters</div>
                    </div>
                    <div>
                      <div className="font-medium mb-1">Alphabets Used: {new Set(cipherKey.split('').map(c => getAlphabetIndex(c))).size}</div>
                      <div className="opacity-60">Total Available: 13 alphabets</div>
                    </div>
                    <div>
                      <div className="font-medium mb-1">Key Repeats: {Math.ceil(inputText.replace(/[^A-Z]/g, '').length / cipherKey.replace(/[^A-Z]/g, '').length)}</div>
                      <div className="opacity-60">Text Length: {inputText.replace(/[^A-Z]/g, '').length} chars</div>
                    </div>
                  </div>
                  
                  {/* Key mapping visualization */}
                  {showKeyMapping && (
                    <div className="mt-3">
                      <div className="text-xs font-medium mb-2">Key Letter Groups:</div>
                      <div className="flex flex-wrap gap-2">
                        {cipherKey.split('').map((char, index) => (
                          <div
                            key={index}
                            className={`px-2 py-1 rounded text-xs font-mono ${
                              isDarkMode ? 'bg-indigo-600 text-white' : 'bg-indigo-100 text-indigo-800'
                            }`}
                          >
                            {char} → {getKeyGroup(char)}
                          </div>
                        ))}
                      </div>
                      <div className="text-xs opacity-60 mt-2">
                        Each letter pair (AB, CD, EF, etc.) uses the same substitution alphabet
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Table Preview */}
              {showTable && portaTable.length > 0 && (
                <div className={`p-4 rounded ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'} mb-4`}>
                  <h4 className="text-sm font-medium mb-3">Porta Cipher Alphabets (13 Total):</h4>
                  <div className="overflow-x-auto">
                    <div className="space-y-1 text-xs font-mono">
                      {['AB', 'CD', 'EF', 'GH', 'IJ', 'KL', 'MN', 'OP', 'QR', 'ST', 'UV', 'WX', 'YZ'].slice(0, 5).map((pair, index) => (
                        <div key={index} className="flex items-center space-x-2">
                          <div className={`w-8 text-center font-bold ${
                            isDarkMode ? 'text-indigo-400' : 'text-indigo-600'
                          }`}>
                            {pair}:
                          </div>
                          <div className={`flex space-x-px ${
                            isDarkMode ? 'text-white' : 'text-gray-900'
                          }`}>
                            {portaTable[index]?.map((letter, letterIndex) => (
                              <div
                                key={letterIndex}
                                className={`w-5 h-5 flex items-center justify-center text-xs border ${
                                  isDarkMode ? 'bg-gray-600 border-gray-500' : 'bg-white border-gray-300'
                                }`}
                              >
                                {letter}
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="text-xs opacity-60 mt-3 text-center">
                    Showing first 5 of 13 substitution alphabets • Each key letter pair uses one alphabet
                  </div>
                </div>
              )}
            </div>

            {/* Text Areas */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Input */}
              <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg p-6 shadow-lg`}>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold">Input Text</h3>
                  <div className="text-sm opacity-60">
                    {inputText.length} characters
                  </div>
                </div>
                <textarea
                  value={inputText}
                  onChange={(e) => setInputText(e.target.value)}
                  placeholder="Enter text to encrypt or decrypt using Porta cipher..."
                  className={`w-full h-64 p-4 rounded border resize-none ${
                    isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-300'
                  }`}
                />
                <div className="mt-2 flex items-center space-x-4">
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="file"
                      accept=".txt"
                      onChange={handleFileUpload}
                      className="sr-only"
                    />
                    <Upload size={16} className="mr-1" />
                    <span className="text-sm">Upload File</span>
                  </label>
                </div>
              </div>

              {/* Output */}
              <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg p-6 shadow-lg`}>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold">Output Text</h3>
                  <div className="flex items-center space-x-2">
                    <div className="text-sm opacity-60">
                      {outputText.length} characters
                    </div>
                    <button
                      onClick={() => copyToClipboard(outputText)}
                      className={`p-2 rounded ${
                        isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'
                      } transition-colors`}
                      title="Copy to clipboard"
                    >
                      <Copy size={16} />
                    </button>
                    <button
                      onClick={() => downloadText(outputText, 'porta-output.txt')}
                      className={`p-2 rounded ${
                        isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'
                      } transition-colors`}
                      title="Download as file"
                    >
                      <Download size={16} />
                    </button>
                  </div>
                </div>
                <textarea
                  value={outputText}
                  readOnly
                  className={`w-full h-64 p-4 rounded border resize-none ${
                    isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-300'
                  }`}
                />
              </div>
            </div>
          </div>
        </main>

        {/* Table Modal */}
        {activeModal === 'table' && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg p-6 max-w-6xl w-full max-h-[80vh] overflow-y-auto`}>
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold flex items-center space-x-2">
                  <Grid3X3 className="text-indigo-500" />
                  <span>Porta Cipher Table</span>
                </h2>
                <button
                  onClick={() => setActiveModal(null)}
                  className={`p-2 rounded ${isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}
                >
                  ×
                </button>
              </div>
              
              <div className="space-y-6">
                <div className="text-center">
                  <div className="text-sm opacity-60 mb-4">
                    13 Substitution Alphabets • Reciprocal Encryption/Decryption
                  </div>
                </div>
                
                {/* Full Table Display */}
                <div className="overflow-x-auto">
                  <div className="space-y-2">
                    {/* Header */}
                    <div className="flex items-center space-x-1">
                      <div className="w-16 text-center text-sm font-bold">Keys</div>
                      <div className="flex space-x-px">
                        {'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('').map((letter, index) => (
                          <div
                            key={index}
                            className={`w-6 h-6 flex items-center justify-center text-xs font-bold border ${
                              isDarkMode ? 'bg-indigo-700 border-indigo-600 text-white' : 'bg-indigo-100 border-indigo-300 text-indigo-900'
                            }`}
                          >
                            {letter}
                          </div>
                        ))}
                      </div>
                    </div>
                    
                    {/* Table rows */}
                    {['AB', 'CD', 'EF', 'GH', 'IJ', 'KL', 'MN', 'OP', 'QR', 'ST', 'UV', 'WX', 'YZ'].map((pair, index) => (
                      <div key={index} className="flex items-center space-x-1">
                        <div className={`w-16 text-center text-sm font-bold border ${
                          isDarkMode ? 'bg-indigo-700 border-indigo-600 text-white' : 'bg-indigo-100 border-indigo-300 text-indigo-900'
                        }`}>
                          {pair}
                        </div>
                        <div className="flex space-x-px">
                          {portaTable[index]?.map((cell, cellIndex) => (
                            <div
                              key={cellIndex}
                              className={`w-6 h-6 flex items-center justify-center text-xs font-mono font-bold border hover:bg-opacity-80 transition-colors cursor-pointer ${
                                isDarkMode ? 'bg-gray-600 border-gray-500 text-white hover:bg-gray-500' : 'bg-white border-gray-400 text-gray-900 hover:bg-gray-100'
                              }`}
                              title={`Key group: ${pair}, Input: ${String.fromCharCode(65 + cellIndex)} → Output: ${cell}`}
                            >
                              {cell}
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                
                <div className="text-center space-y-4">
                  <div className="text-sm opacity-60 space-y-2">
                    <div><strong>How to use the table:</strong></div>
                    <div>• Find your key letter's group (A,B use row 1; C,D use row 2; etc.)</div>
                    <div>• Find your plaintext letter in the top alphabet</div>
                    <div>• The cipher letter is directly below in the key's row</div>
                    <div>• Porta cipher is reciprocal (same process for encrypt/decrypt)</div>
                  </div>
                  
                  {/* Example */}
                  <div className={`p-4 rounded ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                    <div className="text-sm font-medium mb-2">Example:</div>
                    <div className="font-mono text-xs space-y-1">
                      <div><strong>Key letter A (group AB), Plaintext H:</strong> Row AB, column H → Result: {portaTable[0]?.[7]}</div>
                      <div><strong>Key letter C (group CD), Plaintext E:</strong> Row CD, column E → Result: {portaTable[1]?.[4]}</div>
                      <div><strong>Reciprocal property:</strong> Encrypt(H,A) = Decrypt(Encrypt(H,A),A)</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Help Modal */}
        {activeModal === 'help' && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg p-6 max-w-3xl w-full max-h-[80vh] overflow-y-auto`}>
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold flex items-center space-x-2">
                  <Shield className="text-indigo-500" />
                  <span>About Porta Cipher</span>
                </h2>
                <button
                  onClick={() => setActiveModal(null)}
                  className={`p-2 rounded ${isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}
                >
                  ×
                </button>
              </div>
              
              <div className="space-y-4 text-sm">
                <div>
                  <h3 className="font-semibold mb-2">What is the Porta Cipher?</h3>
                  <p>The Porta Cipher is a polyalphabetic substitution cipher invented by Giovanni Battista Porta in 1563. It's notable for being one of the earliest polyalphabetic ciphers and for its reciprocal property - the same process is used for both encryption and decryption. The cipher uses 13 different substitution alphabets, with each pair of key letters (AB, CD, EF, etc.) corresponding to one alphabet.</p>
                </div>
                
                <div>
                  <h3 className="font-semibold mb-2">How it Works</h3>
                  <ol className="list-decimal list-inside space-y-1">
                    <li>Create 13 substitution alphabets for key letter pairs</li>
                    <li>Key letters A,B use alphabet 1; C,D use alphabet 2; etc.</li>
                    <li>For each plaintext letter, find the corresponding key letter</li>
                    <li>Use the key's alphabet to substitute the plaintext letter</li>
                    <li>The same process decrypts the message (reciprocal)</li>
                    <li>Repeat the key as needed for the entire message</li>
                  </ol>
                </div>
                
                <div>
                  <h3 className="font-semibold mb-2">Key Letter Grouping</h3>
                  <div className={`p-3 rounded ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                    <div className="space-y-2 text-xs">
                      <div><strong>Alphabet 1:</strong> A, B → NOPQRSTUVWXYZABCDEFGHIJKLM</div>
                      <div><strong>Alphabet 2:</strong> C, D → OPQRSTUVWXYZNMABCDEFGHIJKL</div>
                      <div><strong>Alphabet 3:</strong> E, F → PQRSTUVWXYZNOLMABCDEFGHIJK</div>
                      <div className="opacity-60">... and so on for all 13 alphabets</div>
                      <div className="text-xs opacity-60 mt-2">
                        Each alphabet is a different arrangement of the 26 letters
                      </div>
                    </div>
                  </div>
                </div>
                
                <div>
                  <h3 className="font-semibold mb-2">Encryption Example</h3>
                  <div className={`p-3 rounded ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                    <div className="font-mono text-xs space-y-1">
                      <div><strong>Plaintext:</strong> "HELLO"</div>
                      <div><strong>Key:</strong> "PORTA" (repeated as "PORTAP")</div>
                      <div><strong>Process:</strong></div>
                      <div>• H with key P (group OP): Use alphabet 8 → Result</div>
                      <div>• E with key O (group OP): Use alphabet 8 → Result</div>
                      <div>• L with key R (group QR): Use alphabet 9 → Result</div>
                      <div>• And so on...</div>
                      <div><strong>Ciphertext:</strong> Encoded message</div>
                    </div>
                  </div>
                </div>
                
                <div>
                  <h3 className="font-semibold mb-2">Historical Background</h3>
                  <ul className="space-y-1">
                    <li><strong>Inventor:</strong> Giovanni Battista Porta (1535-1615)</li>
                    <li><strong>Year:</strong> 1563 (in "De Furtivis Literarum Notis")</li>
                    <li><strong>Origin:</strong> Renaissance Italy</li>
                    <li><strong>Innovation:</strong> First practical polyalphabetic cipher</li>
                    <li><strong>Purpose:</strong> Diplomatic and scholarly correspondence</li>
                    <li><strong>Significance:</strong> Foundation for later polyalphabetic systems</li>
                  </ul>
                </div>
                
                <div>
                  <h3 className="font-semibold mb-2">Giovanni Battista Porta</h3>
                  <p>Giovanni Battista Porta was an Italian scholar, playwright, and polymath during the Renaissance. His work "De Furtivis Literarum Notis" (On the Secret Characters of Letters) was one of the first comprehensive treatises on cryptography. Porta's cipher represented a significant advancement over simple substitution ciphers and influenced cryptographic development for centuries.</p>
                </div>
                
                <div>
                  <h3 className="font-semibold mb-2">Reciprocal Property</h3>
                  <p>The Porta cipher's most important feature is its reciprocal nature. Unlike many ciphers that require different processes for encryption and decryption, the Porta cipher uses the same substitution table for both operations. This made it particularly practical for Renaissance-era users who had to perform encryption manually.</p>
                </div>
                
                <div>
                  <h3 className="font-semibold mb-2">Renaissance Context</h3>
                  <p>During the Renaissance, the increasing complexity of diplomatic relations, trade, and scholarly correspondence created a need for more sophisticated encryption methods. Porta's cipher emerged during this period as a practical solution that balanced security with usability, making it accessible to educated users of the time.</p>
                </div>
                
                <div>
                  <h3 className="font-semibold mb-2">Advantages</h3>
                  <ul className="space-y-1">
                    <li>Reciprocal encryption/decryption process</li>
                    <li>Stronger than monoalphabetic substitution</li>
                    <li>Relatively simple to implement manually</li>
                    <li>Only 13 alphabets to memorize (vs 26 for full polyalphabetic)</li>
                    <li>Resistant to basic frequency analysis</li>
                    <li>Practical for Renaissance-era use</li>
                  </ul>
                </div>
                
                <div>
                  <h3 className="font-semibold mb-2">Weaknesses</h3>
                  <ul className="space-y-1">
                    <li>Limited to 13 substitution alphabets</li>
                    <li>Key letter pairs reduce cipher alphabet diversity</li>
                    <li>Vulnerable to frequency analysis with sufficient text</li>
                    <li>Susceptible to known plaintext attacks</li>
                    <li>Periodic key repetition creates patterns</li>
                    <li>Not suitable for modern security requirements</li>
                  </ul>
                </div>
                
                <div>
                  <h3 className="font-semibold mb-2">Cryptanalysis</h3>
                  <ul className="space-y-1">
                    <li><strong>Frequency Analysis:</strong> Look for patterns in letter pair frequencies</li>
                    <li><strong>Key Length Detection:</strong> Use index of coincidence methods</li>
                    <li><strong>Known Plaintext:</strong> Exploit known message segments</li>
                    <li><strong>Pattern Recognition:</strong> Identify repeated cipher segments</li>
                    <li><strong>Statistical Methods:</strong> Compare against expected language patterns</li>
                  </ul>
                </div>
                
                <div>
                  <h3 className="font-semibold mb-2">Modern Applications</h3>
                  <ul className="space-y-1">
                    <li>Cryptography education and history</li>
                    <li>Understanding polyalphabetic principles</li>
                    <li>Renaissance cryptography demonstrations</li>
                    <li>Puzzle creation and recreational cryptography</li>
                    <li>Historical cipher analysis</li>
                    <li>Teaching reciprocal cipher properties</li>
                  </ul>
                </div>
                
                <div>
                  <h3 className="font-semibold mb-2">Comparison with Other Ciphers</h3>
                  <ul className="space-y-1">
                    <li><strong>vs Vigenère:</strong> Reciprocal property, fewer alphabets</li>
                    <li><strong>vs Beaufort:</strong> Different alphabet arrangement, similar reciprocal nature</li>
                    <li><strong>vs Caesar:</strong> Much more sophisticated, polyalphabetic</li>
                    <li><strong>vs Playfair:</strong> Different approach, letter vs digraph substitution</li>
                    <li><strong>vs Alberti:</strong> Simpler implementation, manual vs mechanical</li>
                  </ul>
                </div>
                
                <div>
                  <h3 className="font-semibold mb-2">Educational Value</h3>
                  <p>The Porta cipher is excellent for teaching the evolution from monoalphabetic to polyalphabetic ciphers. It demonstrates how Renaissance scholars approached cryptographic problems and shows the development of practical encryption systems that balanced security with usability in pre-computer eras.</p>
                </div>
                
                <div>
                  <h3 className="font-semibold mb-2">Historical Impact</h3>
                  <ul className="space-y-1">
                    <li>Influenced development of later polyalphabetic ciphers</li>
                    <li>Demonstrated practical polyalphabetic encryption</li>
                    <li>Established principles still used in modern cryptography</li>
                    <li>Contributed to Renaissance intellectual advancement</li>
                    <li>Inspired mathematical approaches to cipher design</li>
                  </ul>
                </div>
                
                <div>
                  <h3 className="font-semibold mb-2">Security Level</h3>
                  <p className="text-orange-600">⚠️ <strong>Historical Security:</strong> The Porta cipher provided excellent security for Renaissance-era communications and was significantly stronger than simple substitution ciphers. However, it is easily broken by modern cryptanalytic techniques and should not be used for actual security purposes today. It remains valuable for understanding cryptographic history and principles.</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Settings Modal */}
        {activeModal === 'settings' && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg p-6 max-w-md w-full max-h-[80vh] overflow-y-auto`}>
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold">Advanced Options</h2>
                <button
                  onClick={() => setActiveModal(null)}
                  className={`p-2 rounded ${isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}
                >
                  ×
                </button>
              </div>
              
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium">Show Table</label>
                  <input
                    type="checkbox"
                    checked={showTable}
                    onChange={(e) => setShowTable(e.target.checked)}
                    className="rounded"
                  />
                </div>
                
                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium">Group Output</label>
                  <input
                    type="checkbox"
                    checked={groupOutput}
                    onChange={(e) => setGroupOutput(e.target.checked)}
                    className="rounded"
                  />
                </div>
                
                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium">Key Indicators</label>
                  <input
                    type="checkbox"
                    checked={keyIndicators}
                    onChange={(e) => setKeyIndicators(e.target.checked)}
                    className="rounded"
                  />
                </div>
                
                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium">Show Key Mapping</label>
                  <input
                    type="checkbox"
                    checked={showKeyMapping}
                    onChange={(e) => setShowKeyMapping(e.target.checked)}
                    className="rounded"
                  />
                </div>
                
                {groupOutput && (
                  <div>
                    <label className="block text-sm font-medium mb-2">Group Size</label>
                    <input
                      type="range"
                      min="3"
                      max="10"
                      value={groupSize}
                      onChange={(e) => setGroupSize(parseInt(e.target.value))}
                      className="w-full"
                    />
                    <div className="text-xs opacity-60 mt-1">
                      Characters per group: {groupSize}
                    </div>
                  </div>
                )}
                
                <div className="pt-4 border-t">
                  <h3 className="text-sm font-medium mb-3">Historical Keys</h3>
                  <div className="grid grid-cols-2 gap-2">
                    {['PORTA', 'GIOVANNI', 'BATTISTA', 'CIPHER', 'SECRET', 'RENAISSANCE', 'ITALIAN', 'CRYPTO', 'ENCODE', 'DECODE', 'CLASSIC', 'POLYALPHA'].map((word, index) => (
                      <button
                        key={index}
                        onClick={() => setCipherKey(word)}
                        className={`p-2 text-xs rounded border ${
                          isDarkMode ? 'bg-gray-700 border-gray-600 hover:bg-gray-600' : 'bg-gray-100 border-gray-300 hover:bg-gray-200'
                        } transition-colors`}
                      >
                        {word}
                      </button>
                    ))}
                  </div>
                </div>
                
                <div className="pt-4 border-t">
                  <h3 className="text-sm font-medium mb-3">Cipher Presets</h3>
                  <div className="space-y-2">
                    <button
                      onClick={() => {
                        setCipherKey('PORTA');
                        setGroupOutput(true);
                        setGroupSize(5);
                        setKeyIndicators(true);
                        setShowKeyMapping(true);
                        setShowTable(true);
                      }}
                      className={`w-full p-2 text-sm rounded border ${
                        isDarkMode ? 'bg-indigo-600 border-indigo-500 hover:bg-indigo-700 text-white' : 'bg-indigo-500 border-indigo-400 hover:bg-indigo-600 text-white'
                      } transition-colors`}
                    >
                      Classical Renaissance
                    </button>
                    
                    <button
                      onClick={() => {
                        setCipherKey('GIOVANNI');
                        setGroupOutput(true);
                        setGroupSize(4);
                        setKeyIndicators(true);
                        setShowKeyMapping(false);
                        setShowTable(false);
                      }}
                      className={`w-full p-2 text-sm rounded border ${
                        isDarkMode ? 'bg-purple-600 border-purple-500 hover:bg-purple-700 text-white' : 'bg-purple-500 border-purple-400 hover:bg-purple-600 text-white'
                      } transition-colors`}
                    >
                      Historical Scholar
                    </button>
                    
                    <button
                      onClick={() => {
                        setCipherKey('SIMPLE');
                        setGroupOutput(false);
                        setKeyIndicators(false);
                        setShowKeyMapping(false);
                        setShowTable(false);
                      }}
                      className={`w-full p-2 text-sm rounded border ${
                        isDarkMode ? 'bg-green-600 border-green-500 hover:bg-green-700 text-white' : 'bg-green-500 border-green-400 hover:bg-green-600 text-white'
                      } transition-colors`}
                    >
                      Modern Educational
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Analytics Modal */}
        {activeModal === 'analytics' && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg p-6 max-w-3xl w-full max-h-[80vh] overflow-y-auto`}>
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold">Analytics Dashboard</h2>
                <button
                  onClick={() => setActiveModal(null)}
                  className={`p-2 rounded ${isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}
                >
                  ×
                </button>
              </div>
              
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                <div className={`p-4 rounded ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                  <div className="text-2xl font-bold text-green-500">{stats.totalEncryptions}</div>
                  <div className="text-sm">Total Encryptions</div>
                </div>
                <div className={`p-4 rounded ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                  <div className="text-2xl font-bold text-blue-500">{stats.totalDecryptions}</div>
                  <div className="text-sm">Total Decryptions</div>
                </div>
                <div className={`p-4 rounded ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                  <div className="text-2xl font-bold text-purple-500">{stats.polyalphabeticOperations}</div>
                  <div className="text-sm">Polyalphabetic Ops</div>
                </div>
                <div className={`p-4 rounded ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                  <div className="text-2xl font-bold text-orange-500">{stats.totalCharactersProcessed}</div>
                  <div className="text-sm">Characters Processed</div>
                </div>
                <div className={`p-4 rounded ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                  <div className="text-2xl font-bold text-pink-500">{stats.uniqueKeys.size}</div>
                  <div className="text-sm">Unique Keys</div>
                </div>
                <div className={`p-4 rounded ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                  <div className="text-2xl font-bold text-cyan-500">{stats.averageKeyLength}</div>
                  <div className="text-sm">Avg Key Length</div>
                </div>
                <div className={`p-4 rounded ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                  <div className="text-2xl font-bold text-amber-500">{stats.totalTablesUsed}</div>
                  <div className="text-sm">Tables Used</div>
                </div>
                <div className={`p-4 rounded ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                  <div className="text-2xl font-bold text-red-500">{stats.longestText}</div>
                  <div className="text-sm">Longest Text</div>
                </div>
              </div>
              
              <div>
                <h3 className="text-lg font-semibold mb-4">Recent Operations</h3>
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {history.length > 0 ? history.slice(0, 15).map(item => (
                    <div key={item.id} className={`p-3 rounded ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                      <div className="flex items-center justify-between mb-1">
                        <div className="flex items-center space-x-2">
                          <span className={`text-xs px-2 py-1 rounded ${
                            item.operation === 'encrypt' ? 'bg-green-500 text-white' : 'bg-blue-500 text-white'
                          }`}>
                            {item.operation.toUpperCase()}
                          </span>
                          <span className="text-xs px-2 py-1 rounded bg-purple-500 text-white">
                            {item.keyRepeats} repeats
                          </span>
                          <span className="text-xs px-2 py-1 rounded bg-indigo-500 text-white">
                            {item.key}
                          </span>
                        </div>
                        <span className="text-xs opacity-60">
                          {item.timestamp.toLocaleTimeString()}
                        </span>
                      </div>
                      <div className="text-sm">
                        <div className="truncate font-mono">{item.input}</div>
                        <div className="truncate opacity-60 font-mono">→ {item.output}</div>
                      </div>
                    </div>
                  )) : (
                    <div className="text-center py-8 opacity-60">
                      <BookOpen size={32} className="mx-auto mb-2" />
                      <div>No operations performed yet</div>
                      <div className="text-xs">Start encrypting or decrypting to see analytics</div>
                    </div>
                  )}
                </div>
              </div>
              
              {history.length > 0 && (
                <div className="mt-6 pt-4 border-t">
                  <h3 className="text-sm font-medium mb-2">Cipher Statistics</h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-xs">
                    <div>
                      <div className="text-purple-500">Most Used Key:</div>
                      <div>
                        {history.length > 0 
                          ? (() => {
                              const keyCounts = history.reduce((acc, item) => {
                                acc[item.key] = (acc[item.key] || 0) + 1;
                                return acc;
                              }, {} as Record<string, number>);
                              return Object.keys(keyCounts).reduce((a, b) => keyCounts[a] > keyCounts[b] ? a : b);
                            })()
                          : 'N/A'
                        }
                      </div>
                    </div>
                    <div>
                      <div className="text-green-500">Encrypt/Decrypt Ratio:</div>
                      <div>{stats.totalOperations > 0 ? Math.round((stats.totalEncryptions / stats.totalOperations) * 100) : 0}% Encrypt</div>
                    </div>
                    <div>
                      <div className="text-blue-500">Avg Key Repeats:</div>
                      <div>{stats.totalOperations > 0 ? Math.round(history.reduce((sum, item) => sum + item.keyRepeats, 0) / history.length) : 0}</div>
                    </div>
                    <div>
                      <div className="text-orange-500">Renaissance Efficiency:</div>
                      <div>{stats.totalOperations > 0 ? Math.round((stats.polyalphabeticOperations / stats.totalOperations) * 100) : 0}% Advanced</div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default PortaCipherApp;